/*
 * A price that flashes when it moves.
 *
 * The flash is the whole point. On a table of twenty instruments, a number
 * quietly changing from 1,432.50 to 1,432.75 is invisible; a cell that washes
 * green for half a second is not, and it says which way without being read.
 *
 * RESTARTING THE ANIMATION IS THE FIDDLY PART. A CSS animation does not replay
 * because a class was re-applied — the browser sees the same class on the same
 * element and does nothing. So each flash carries an incrementing key, which
 * makes React replace the element and the animation runs from the start. That
 * is why two consecutive upticks both flash instead of only the first.
 *
 * `stale` greys the number rather than hiding it. The last known price is
 * still the most useful thing to show; what must not happen is showing it as
 * though it were current.
 */

import { useEffect, useRef, useState } from "react";

import { useLive } from "../api/stream";
import { price as fmtPrice } from "../lib/format";

interface Props {
  token: number;
  /** Shown until the first tick — the entry price, or a REST quote. */
  fallback?: number | null;
  className?: string;
}

/** The bare number, flashing. Used inside a table cell. */
export function LivePrice({ token, fallback, className }: Props) {
  const live = useLive();
  const quote = live.quote(token);
  const value = quote?.last ?? fallback ?? null;

  const previous = useRef<number | null>(null);
  const [flash, setFlash] = useState<{ dir: "up" | "down"; key: number } | null>(null);
  const flashKey = useRef(0);

  useEffect(() => {
    if (value === null) return;
    const before = previous.current;
    previous.current = value;
    if (before === null || before === value) return;
    flashKey.current += 1;
    setFlash({ dir: value > before ? "up" : "down", key: flashKey.current });
  }, [value]);

  useEffect(() => {
    if (!flash) return;
    const timer = window.setTimeout(() => setFlash(null), 700);
    return () => window.clearTimeout(timer);
  }, [flash]);

  const classes = [
    "num",
    className,
    live.stale ? "stale" : "",
    flash ? (flash.dir === "up" ? "up" : "down") : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <span key={flash?.key ?? 0} className={classes}>
      {fmtPrice(value)}
    </span>
  );
}

/**
 * The table cell itself, so the wash covers the whole cell rather than the
 * digits. `tick-up` / `tick-down` carry the animation; see base.css.
 */
export function LivePriceCell({ token, fallback }: Props) {
  const live = useLive();
  const quote = live.quote(token);
  const value = quote?.last ?? fallback ?? null;

  const previous = useRef<number | null>(null);
  const [flash, setFlash] = useState<{ dir: "up" | "down"; key: number } | null>(null);
  const flashKey = useRef(0);

  useEffect(() => {
    if (value === null) return;
    const before = previous.current;
    previous.current = value;
    if (before === null || before === value) return;
    flashKey.current += 1;
    setFlash({ dir: value > before ? "up" : "down", key: flashKey.current });
  }, [value]);

  useEffect(() => {
    if (!flash) return;
    const timer = window.setTimeout(() => setFlash(null), 700);
    return () => window.clearTimeout(timer);
  }, [flash]);

  return (
    <td
      key={flash?.key ?? 0}
      className={`num ${flash ? `tick-${flash.dir}` : ""} ${live.stale ? "stale" : ""}`}
    >
      {fmtPrice(value)}
    </td>
  );
}

/**
 * The day's move for one instrument, as a signed figure and a percentage.
 *
 * Both carry the sign as well as the colour, because roughly one man in twelve
 * cannot separate the two colours reliably and this is not a number to guess at.
 */
export function LiveChange({ token }: { token: number }) {
  const live = useLive();
  const quote = live.quote(token);
  if (!quote || !quote.close) return <span className="faint">—</span>;

  const tone = quote.change > 0 ? "up" : quote.change < 0 ? "down" : "flat";
  const arrow = quote.change > 0 ? "▲" : quote.change < 0 ? "▼" : "•";

  return (
    <span className={`num ${tone} ${live.stale ? "stale" : ""}`}>
      {arrow} {Math.abs(quote.change).toFixed(2)}{" "}
      <span className="small">({Math.abs(quote.changePct).toFixed(2)}%)</span>
    </span>
  );
}
